// services
import { createRoute, useEntityUrl } from 'framework/routing';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { useKernelApi } from 'infrastructure/api';
import { useServiceService } from 'domain/shared/service';
import { useEmployeeService, EMPLOYEE_ROUTE_PATHS, useEmployeeUrl } from 'domain/shared/employee';
// validation
import EMPLOYEE_DAVINCI_ROLES_VALIDATION from 'domain/people/employee/CareerCapabilities/companyRoles/create/rolesValidation';
import { Role } from 'domain/shared/core/constants';

type SearchParams = {
  pageNumber: number;
  sort?: any;
  employeeAlias?: Nullable<string>;
  pageSize: number;
};

// permissions
export const READ_EMPLOYEE_COMPANY_ROLES = 'READ_EMPLOYEE_COMPANY_ROLES';
export const UPDATE_EMPLOYEE_COMPANY_ROLES = 'UPDATE_EMPLOYEE_COMPANY_ROLES';

export const IGNORE_ROLES_ON_FE = [
  Role.COMPANY_STAFFING_COORDINATOR,
  Role.COMPANY_VACANCY_MANAGER,
  Role.EMPLOYEE_MENTOR,
  Role.EVENT_MANAGER,
  Role.PERFORMANCE_REPORT_REVIEWER,
  Role.TIMELINE_MANAGER,
];

// urls
export const COMPANY_ROLES_PATH = '/companies/roles';
export const ROLE_TO_PATH = {
  [Role.CEO]: Role.CEO.toLowerCase(),
  [Role.CFO]: Role.CFO.toLowerCase(),
  [Role.CTO]: Role.CTO.toLowerCase(),
  [Role.COO]: Role.COO.toLowerCase(),
  [Role.LEAD_RECRUITER]: 'leadrecruiter',
  [Role.VP_OF_SALES]: 'vpofsales',
  [Role.MARKETER]: Role.MARKETER.toLowerCase(),
  [Role.HR_MANAGER]: 'hrmanagers',
  [Role.EMPLOYEE]: 'employees',
  [Role.RECRUITER]: 'recruiters',
  [Role.EDUCATION_MANAGER]: 'educationmanagers',
  [Role.PRESALE_COORDINATOR]: 'presale-coordinators',
  [Role.MARKETING_MANAGER]: 'marketing-managers',
  [Role.PEOPLE_MANAGER]: 'people-managers',
  [Role.DELIVERY_MANAGER]: 'delivery-managers',
};

// dtos
export const ROLE_TO_DTO_FIELD = {
  [Role.CEO]: 'ceoId',
  [Role.CFO]: 'cfoId',
  [Role.CTO]: 'ctoId',
  [Role.COO]: 'cooId',
  [Role.LEAD_RECRUITER]: 'leadRecruiterIds',
  [Role.VP_OF_SALES]: 'vpOfSalesId',
  [Role.MARKETER]: 'marketerId',
  [Role.HR_MANAGER]: 'hrManagersIds',
  [Role.EMPLOYEE]: 'employeesIds',
  [Role.RECRUITER]: 'recruitersIds',
  [Role.EDUCATION_MANAGER]: 'educationManagersIds',
  [Role.PRESALE_COORDINATOR]: 'presaleCoordinatorsIds',
  [Role.MARKETING_MANAGER]: 'marketingManagersIds',
  [Role.PEOPLE_MANAGER]: 'peopleManagersIds',
  [Role.DELIVERY_MANAGER]: 'deliveryManagersIds',
};

// buttons
export const ROLES_ALLOWED_TO_REMOVE = new Set([
  Role.HR_MANAGER,
  Role.EMPLOYEE,
  Role.RECRUITER,
  Role.EDUCATION_MANAGER,
  Role.PRESALE_COORDINATOR,
  Role.MARKETING_MANAGER,
  Role.PEOPLE_MANAGER,
  Role.DELIVERY_MANAGER,
]);

export const useCompanyRolesRoute = () => {
  return createRoute({ listRoute: `/roles` });
};

export const useCompanyRolesUrl = () => {
  const { getSingleEntityUrl, getCareerCapabilitiesUrl } = useEmployeeUrl();
  const employeeLink = getSingleEntityUrl();

  const baseUrls = useEntityUrl({
    baseLink: `${employeeLink}/${EMPLOYEE_ROUTE_PATHS.CAREER_CAPABILITIES}/roles`,
  });

  return {
    ...baseUrls,
    getListUrl: getCareerCapabilitiesUrl,
  };
};

export const useCompanyRolesService = () => {
  const { sendGetRequest, sendPostRequest, sendPutRequest, sendDeleteRequest } = useKernelApi();
  const { isServiceRole, addServiceRoleToEmployee, removeServiceRoleFromEmployee } = useServiceService();
  const { getEmployeeCompanyId } = useEmployeeService();

  const currentAlias = useEmployeeId();

  const mapToSearchResponse = (roles: any[]) => ({
    totalElements: roles.length,
    result: roles,
    totalPages: 1,
    pageNumber: 1,
  });

  const searchRolesByAlias = async ({ employeeAlias = currentAlias }: SearchParams) => {
    const response = await sendGetRequest(`/employees/${employeeAlias}/roles`);
    const companyRoleNames: string[] = await response.json();

    const roles = companyRoleNames.map(role => ({ name: role }));

    return mapToSearchResponse(roles);
  };

  const searchAll = async () => {
    const response = await sendGetRequest(COMPANY_ROLES_PATH);
    const companyRoleNames: string[] = await response.json();

    return mapToSearchResponse(companyRoleNames);
  };

  const getUpdateEmployeeRolesDto = (role: Role) => {
    let dto;

    switch (role) {
      case Role.CEO:
      case Role.CTO:
      case Role.COO:
      case Role.CFO:
      case Role.VP_OF_SALES:
      case Role.MARKETER:
        dto = { [ROLE_TO_DTO_FIELD[role]]: currentAlias };
        break;

      case Role.HR_MANAGER:
      case Role.EMPLOYEE:
      case Role.RECRUITER:
      case Role.EDUCATION_MANAGER:
      case Role.PRESALE_COORDINATOR:
      case Role.MARKETING_MANAGER:
      case Role.PEOPLE_MANAGER:
      case Role.DELIVERY_MANAGER:
      case Role.LEAD_RECRUITER:
        dto = { [ROLE_TO_DTO_FIELD[role]]: [currentAlias] };
        break;

      default:
        dto = null;
    }

    return dto;
  };

  const sendUpdateEmployeeRolesRequest = async (url: string, dto: any, role: Role) => {
    let response;

    switch (role) {
      case Role.CEO:
      case Role.CFO:
      case Role.CTO:
      case Role.COO:
      case Role.LEAD_RECRUITER:
      case Role.VP_OF_SALES:
      case Role.MARKETER:
        response = await sendPutRequest(url, dto);
        break;

      case Role.HR_MANAGER:
      case Role.EMPLOYEE:
      case Role.RECRUITER:
      case Role.EDUCATION_MANAGER:
      case Role.PRESALE_COORDINATOR:
      case Role.MARKETING_MANAGER:
      case Role.PEOPLE_MANAGER:
      case Role.DELIVERY_MANAGER:
        response = await sendPostRequest(url, dto);
        break;

      default:
        response = Promise.reject(new Error(`Unknown role: '${role}'.`));
    }

    return response;
  };

  const addRoleToEmployee = async (formData: any) => {
    const role = formData.role as Role;

    if (isServiceRole(role)) {
      return addServiceRoleToEmployee(role, currentAlias);
    }

    const companyId = await getEmployeeCompanyId(currentAlias!);
    const companyRoleUrl = `/companies/${companyId}/${ROLE_TO_PATH[role as Role.CTO]}`;
    const updateDto = getUpdateEmployeeRolesDto(role);

    return sendUpdateEmployeeRolesRequest(companyRoleUrl, updateDto, role);
  };

  const removeRoleFromEmployee = async (role: Role) => {
    const companyId = await getEmployeeCompanyId(currentAlias!);

    if (isServiceRole(role)) {
      return removeServiceRoleFromEmployee(role, currentAlias);
    }

    const removeRoleUrl = `/companies/${companyId}/${ROLE_TO_PATH[role as Role.CTO]}/${currentAlias}`;

    return sendDeleteRequest(removeRoleUrl);
  };

  const getValidationSchema = async () => EMPLOYEE_DAVINCI_ROLES_VALIDATION;

  return {
    sendUpdateEmployeeRolesRequest,
    getUpdateEmployeeRolesDto,
    getValidationSchema,
    addRoleToEmployee,
    removeRoleFromEmployee,
    searchRolesByAlias,
    searchAll,
  };
};
